import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import useVendorStore from "../HomePage/useVendorStore";
import {
  GRN_STATUS_COMPLETED,
  STATUS_BEGIN,
  STATUS_DONE,
  STATUS_FAIL,
  PO_ORDER_TAG_MANDI_DD,
  PO_ORDER_TAG_DIRECT_DELIVERY,
  PAYMENT_STATUS_NOT_PAID,
  GRN_OR_FILTER_VALUES,
} from "utils/constants/others";
import getHexColor from "utils/getHexColor";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;
const EMPTY_VALUE = "—";

const formatAmount = (amount) =>
  amount === null || amount === undefined ? EMPTY_VALUE : `₹${Number(amount).toLocaleString("en-IN")}`;

const formatPaymentDate = (date) => {
  if (!date || !moment(date).isValid()) return EMPTY_VALUE;
  return moment(date).format("DD MMM 'YY");
};

const toApiDate = (date) => (date ? moment(date).format("YYYY-MM-DD") : "");

/**
 * Owns all GRN list state for both layouts.
 * - Mobile appends pages (`loadNextPage`, infinite scroll).
 * - Desktop replaces the page (`goToPage`) and applies the status / GRN date filters on "Apply filters".
 */
export default function useGrnListLogic() {
  const poGrnMappings = useVendorStore((state) => state.poGrnList);
  const getPoGrnMappings = useVendorStore((state) => state.getPoGrnMappings);
  const getPoGrnMappingStatus = useVendorStore((state) => state.getPoGrnMappingStatus);
  const poGrnHasNextPage = useVendorStore((state) => state.poGrnHasNextPage);
  const storedGrnListSearchFilters = useVendorStore((state) => state.grnListSearchFilters);
  const setGrnListSearchFilters = useVendorStore((state) => state.setGrnListSearchFilters);
  const outletId = window.localStorage.getItem("outlet_id");

  const [showLoader, setShowLoader] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const abortControllerRef = useRef(null);
  const hasMountedSearchEffect = useRef(false);

  const [grnListSearchFilters, setGrnListSearchFiltersLocal] = useState(() => storedGrnListSearchFilters);
  const [debouncedSearchString, setDebouncedSearchString] = useState(() =>
    storedGrnListSearchFilters.searchString.trim()
  );
  const { orFilters, isSearchOpen, searchString, grnDate = "" } = grnListSearchFilters;
  // A search ignores the status chips.
  const effectiveOrFilters = debouncedSearchString.length > 0 ? [] : orFilters;

  // Desktop filter card: edits stay in draft until "Apply filters".
  const [draftOrFilters, setDraftOrFilters] = useState(orFilters);
  const [draftGrnDate, setDraftGrnDate] = useState(grnDate ? new Date(grnDate) : null);

  useEffect(() => {
    setGrnListSearchFilters(grnListSearchFilters);
  }, [grnListSearchFilters, setGrnListSearchFilters]);

  const fetchPoGrnMappings = useCallback((page, isFirstPage) => {
    const otherFilterSelected = effectiveOrFilters.some((item) => item == GRN_OR_FILTER_VALUES.SELLER_STATUS_APPROVED || item == GRN_OR_FILTER_VALUES.SELLER_STATUS_NON_APPROVED);
    const paidSelected = effectiveOrFilters.includes(GRN_OR_FILTER_VALUES.PAYMENT_STATUS_PAID);
    // To not show paid GRNs when with approved and confirmation pending grns
    const paymentStatus = otherFilterSelected && !paidSelected ? PAYMENT_STATUS_NOT_PAID : "";

    let signal;
    if (isFirstPage) {
      // Cancel any in-flight first-page request before firing a new one
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      signal = abortControllerRef.current.signal;
    }

    getPoGrnMappings(
      {
        page_number: page,
        grn_status: GRN_STATUS_COMPLETED,
        page_size: PAGE_SIZE,
        outlet_id: outletId,
        fetch_ledger_data: true,
        is_payment_details_required: true,
        or_filters: effectiveOrFilters,
        exclude_purchase_order_tags: `${PO_ORDER_TAG_MANDI_DD},${PO_ORDER_TAG_DIRECT_DELIVERY}`,
        search_string: debouncedSearchString,
        skip_total_page_count: true,
        payment_status: paymentStatus,
        grn_completed_on: grnDate,
      },
      isFirstPage,
      signal
    );
  }, [effectiveOrFilters, outletId, debouncedSearchString, grnDate, getPoGrnMappings]);

  useEffect(() => {
    setPageNumber(1);
    setHasMore(true);
    fetchPoGrnMappings(1, true);
  }, [orFilters, grnDate, fetchPoGrnMappings]);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setDebouncedSearchString(searchString.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(debounceTimeout);
  }, [searchString]);

  useEffect(() => {
    if (!hasMountedSearchEffect.current) {
      hasMountedSearchEffect.current = true;
      return;
    }

    setPageNumber(1);
    setHasMore(true);
    fetchPoGrnMappings(1, true);
  }, [debouncedSearchString, fetchPoGrnMappings]);

  useEffect(() => {
    switch (getPoGrnMappingStatus) {
      case STATUS_BEGIN:
        setShowLoader(true);
        break;
      case STATUS_DONE:
        setHasMore(!!poGrnHasNextPage);
        setShowLoader(false);
        break;
      case STATUS_FAIL:
        setShowLoader(false);
        setShowToast(true);
        break;
      default:
        break;
    }
  }, [getPoGrnMappingStatus, poGrnHasNextPage]);

  const toggleFilter = (filterValue) => {
    setGrnListSearchFiltersLocal((prev) => {
      const updatedFilters = prev.orFilters.includes(filterValue)
        ? prev.orFilters.filter((value) => value !== filterValue)
        : [...prev.orFilters, filterValue];
      return { ...prev, orFilters: updatedFilters };
    });
  };

  const setSearchString = (value) =>
    setGrnListSearchFiltersLocal((prev) => ({ ...prev, searchString: value }));

  const handleSearchToggle = () => {
    setGrnListSearchFiltersLocal((prev) =>
      prev.isSearchOpen
        ? { ...prev, searchString: "", isSearchOpen: false }
        : { ...prev, isSearchOpen: true }
    );
  };

  // ── mobile paging ────────────────────────────────────────────────────────
  const loadNextPage = () => {
    const nextPage = pageNumber + 1;
    fetchPoGrnMappings(nextPage, false);
    setPageNumber(nextPage);
  };

  // ── desktop paging / filters ─────────────────────────────────────────────
  const goToPage = (page) => {
    setPageNumber(page);
    fetchPoGrnMappings(page, true);
  };

  const toggleDraftFilter = (filterValue) =>
    setDraftOrFilters((prev) =>
      prev.includes(filterValue) ? prev.filter((value) => value !== filterValue) : [...prev, filterValue]
    );

  const applyFilters = () =>
    setGrnListSearchFiltersLocal((prev) => ({ ...prev, orFilters: draftOrFilters, grnDate: toApiDate(draftGrnDate) }));

  const clearFilters = () => {
    setDraftOrFilters([]);
    setDraftGrnDate(null);
    setGrnListSearchFiltersLocal((prev) => ({ ...prev, orFilters: [], grnDate: "" }));
  };

  const hasPendingFilterChanges =
    toApiDate(draftGrnDate) !== grnDate ||
    draftOrFilters.length !== orFilters.length ||
    draftOrFilters.some((value) => !orFilters.includes(value));

  const rows = useMemo(
    () =>
      (Array.isArray(poGrnMappings) ? poGrnMappings : []).map((grn) => ({
        id: grn.id,
        purchaseOrderId: grn.purchase_order_id,
        grnNumber: grn.grn_number || EMPTY_VALUE,
        invoiceNumber: grn.supplier_bill_number || EMPTY_VALUE,
        invoiceAmount: formatAmount(grn.supplier_bill_amount),
        grnAmount: formatAmount(grn.grn_amount),
        grnDate: grn.grn_page_data?.grn_completed_on_date_str || EMPTY_VALUE,
        paymentDate: formatPaymentDate(grn.purchase_order_payment_details?.payment_date),
        tags: (grn.grn_page_data?.header_tags || [])
          .filter((tag) => tag?.text)
          .map((tag) => ({ text: tag.text, bg: getHexColor(tag.bg_color), color: getHexColor(tag.color) })),
        _raw: grn,
      })),
    [poGrnMappings]
  );

  const isListLoading = showLoader && pageNumber === 1;
  const isEmpty = !isListLoading && getPoGrnMappingStatus === STATUS_DONE && rows.length === 0;

  return {
    // data
    poGrnMappings,
    rows,
    getPoGrnMappingStatus,
    isListLoading,
    isEmpty,
    hasError: getPoGrnMappingStatus === STATUS_FAIL,
    isSearching: debouncedSearchString.length > 0,
    showToast,
    // search / filters
    orFilters,
    isSearchOpen,
    searchString,
    setSearchString,
    toggleFilter,
    handleSearchToggle,
    // mobile paging
    hasMore,
    loadNextPage,
    pageNumber,
    // desktop paging / filter card
    hasNextPage: !!poGrnHasNextPage,
    goToPage,
    draftOrFilters,
    toggleDraftFilter,
    draftGrnDate,
    setDraftGrnDate,
    hasPendingFilterChanges,
    applyFilters,
    clearFilters,
    refetch: () => fetchPoGrnMappings(1, true),
  };
}
